/**
 * Platform/premium stats (F9/F10) — anonymized aggregates (k=5,
 * buckets below the threshold are dropped). Platform routes are cross-tenant
 * (platform users only); premium routes are the SAME cross-sections tenant-scoped —
 * 403 = premium gate (subscription or 10+ paying clients), handled
 * by the caller.
 */
import type {
  FetchPlatformHealthIssuesArgs,
  FetchPlatformDemographicsArgs,
  FetchPlatformEngagementArgs,
  ApiPlatformHealthIssues,
  ApiPlatformDemographics,
  ApiTenantOrganizationRow,
  ApiPlatformEngagement,
  ApiTenantPricingRow,
} from './types/statsInsights.types';

import { get } from './axios';

export const fetchPlatformDemographics = async (
  args: FetchPlatformDemographicsArgs,
) => {
  const response = await get<ApiPlatformDemographics>(
    '/v2/stats/platform/demographics',
    {
      query: {
        dimensions: args.dimensions.join(','),
        from: args.from,
        metricKey: args.metricKey,
        to: args.to,
      },
    },
  );
  return response ?? null;
};

export const fetchPlatformHealthIssues = async (
  args: FetchPlatformHealthIssuesArgs = {},
) => {
  const response = await get<ApiPlatformHealthIssues>(
    '/v2/stats/platform/health-issues',
    {
      query: {
        dimensions: args.dimensions?.length
          ? args.dimensions.join(',')
          : undefined,
      },
    },
  );
  return response ?? null;
};

export const fetchPlatformEngagement = async (
  args: FetchPlatformEngagementArgs,
) => {
  const response = await get<ApiPlatformEngagement>(
    '/v2/stats/platform/engagement',
    {
      query: {
        dimensions: args.dimensions?.length
          ? args.dimensions.join(',')
          : undefined,
        from: args.from,
        scope: args.scope,
        to: args.to,
      },
    },
  );
  return response ?? null;
};

export const fetchPlatformTenants = async () => {
  const response = await get<ApiTenantPricingRow[]>(
    '/v2/stats/platform/tenants',
  );
  return response ?? null;
};

export const fetchPlatformTenantOrganizations = async (tenantId: string) => {
  const response = await get<ApiTenantOrganizationRow[]>(
    `/v2/stats/platform/tenants/${tenantId}/organizations`,
  );
  return response ?? null;
};

// Task 7.1 — "MyFitWorld Platform Analytics" is per ORGANIZATION (the
// organization pays; the caller needs `stats_premium_read` in it).
export const fetchPremiumDemographics = async (
  organizationId: string,
  args: FetchPlatformDemographicsArgs,
) => {
  const response = await get<ApiPlatformDemographics>(
    `/v2/organizations/${organizationId}/platform-analytics/demographics`,
    {
      query: {
        dimensions: args.dimensions.join(','),
        from: args.from,
        metricKey: args.metricKey,
        to: args.to,
      },
    },
  );
  return response ?? null;
};

export const fetchPremiumHealthIssues = async (
  organizationId: string,
  args: FetchPlatformHealthIssuesArgs = {},
) => {
  const response = await get<ApiPlatformHealthIssues>(
    `/v2/organizations/${organizationId}/platform-analytics/health-issues`,
    {
      query: {
        dimensions: args.dimensions?.length
          ? args.dimensions.join(',')
          : undefined,
      },
    },
  );
  return response ?? null;
};
